import { openMembershipDrawer } from "./membership_drawer.js";

let toastTimer;
let toastRemainingMs = 0;
let toastStartedAt = 0;
const toastPauseReasons = new Set();
let pendingUndo;
let modalTrigger;
let restoreModalFocus = true;

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || "";
}

function favoriteButtons(key) {
  return Array.from(document.querySelectorAll("[data-favorite-toggle]")).filter(
    (button) => button.dataset.favoriteKey === key,
  );
}

function setButtonsBusy(buttons, busy) {
  buttons.forEach((button) => {
    button.disabled = busy;
    button.setAttribute("aria-busy", busy.toString());
  });
}

function updateFavoritesPage(delta, { paginationHidden } = {}) {
  const page = document.querySelector("[data-favorites-page]");
  if (!page) return {};

  const currentCount = Number.parseInt(page.dataset.favoritesCount || "0", 10);
  const remaining = Math.max((Number.isNaN(currentCount) ? 0 : currentCount) + delta, 0);
  page.dataset.favoritesCount = remaining.toString();

  const emptyState = page.querySelector("[data-favorites-empty]");
  const pagination = page.querySelector("[data-favorites-pagination]");
  const cardsRemain = Boolean(page.querySelector("[data-favorite-card]"));
  if (remaining === 0) {
    emptyState?.removeAttribute("hidden");
    pagination?.setAttribute("hidden", "");
  } else {
    emptyState?.setAttribute("hidden", "");
    if (pagination && typeof paginationHidden === "boolean") pagination.hidden = paginationHidden;
  }

  return {
    reloadPath: remaining > 0 && !cardsRemain ? page.dataset.favoritesPath : null,
  };
}

function removeFavoriteCards(buttons) {
  const removableCards = new Set();

  buttons.forEach((button) => {
    if (button.dataset.removeOnUnfavorite === "true") {
      removableCards.add(button.closest("[data-favorite-card]"));
    }
  });

  const cards = Array.from(removableCards).filter(Boolean);
  if (cards.length === 0) return { cards: [], reloadPath: null };

  const pagination = document.querySelector("[data-favorites-pagination]");
  const paginationHidden = pagination?.hidden;
  const cardPositions = cards.map((card) => ({
    card,
    parent: card.parentNode,
    nextSibling: card.nextSibling,
  }));

  cards.forEach((card) => card.remove());
  const { reloadPath } = updateFavoritesPage(-1);

  return { cards: cardPositions, paginationHidden, reloadPath };
}

function setFavoriteState(buttons, favorited, { removeCards = true } = {}) {
  buttons.forEach((button) => {
    button.dataset.favorited = favorited.toString();
    button.setAttribute("aria-pressed", favorited.toString());
    button.setAttribute("aria-label", favorited ? button.dataset.removeLabel : button.dataset.addLabel);
    button.querySelector("[data-favorite-outline]").hidden = favorited;
    button.querySelector("[data-favorite-filled]").hidden = !favorited;
  });

  if (favorited || !removeCards) return { cards: [], reloadPath: null };

  return removeFavoriteCards(buttons);
}

function restoreFavoriteState(undo) {
  let restoredCard = false;

  undo.cards.slice().reverse().forEach(({ card, parent, nextSibling }) => {
    if (!parent?.isConnected) return;

    if (nextSibling?.parentNode === parent) {
      parent.insertBefore(card, nextSibling);
    } else {
      parent.appendChild(card);
    }
    restoredCard = true;
  });

  if (restoredCard) {
    updateFavoritesPage(1, { paginationHidden: undo.paginationHidden });
  }
  setFavoriteState(undo.buttons, true, { removeCards: false });
}

async function requestFavorite(url, method) {
  const response = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
  });
  const data = await response.json();
  if (!response.ok || typeof data.favorited !== "boolean") {
    throw new Error("Favorite request failed");
  }

  return data.favorited;
}

function clearToastTimer() {
  if (toastTimer !== undefined) window.clearTimeout(toastTimer);
  toastTimer = undefined;
}

function scheduleToastTimer() {
  clearToastTimer();
  if (toastPauseReasons.size > 0) return;

  if (toastRemainingMs <= 0) {
    hideToast();
    return;
  }

  toastStartedAt = Date.now();
  toastTimer = window.setTimeout(function () {
    toastTimer = undefined;
    hideToast();
  }, toastRemainingMs);
}

function pauseToast(reason) {
  const toast = document.querySelector("[data-favorite-toast]");
  if (!toast || toast.hidden || toastPauseReasons.has(reason)) return;

  if (toastPauseReasons.size === 0 && toastTimer !== undefined) {
    toastRemainingMs = Math.max(toastRemainingMs - (Date.now() - toastStartedAt), 0);
    clearToastTimer();
  }
  toastPauseReasons.add(reason);
}

function resumeToast(reason) {
  const toast = document.querySelector("[data-favorite-toast]");
  toastPauseReasons.delete(reason);
  if (!toast || toast.hidden || toastPauseReasons.size > 0) return;

  scheduleToastTimer();
}

function hideToast({ reloadEmptyPage = true, force = false } = {}) {
  if (!force && pendingUndo?.inFlight) return;

  const toast = document.querySelector("[data-favorite-toast]");
  if (toast) toast.hidden = true;
  clearToastTimer();
  toastRemainingMs = 0;
  toastPauseReasons.clear();

  const reloadPath = reloadEmptyPage ? pendingUndo?.reloadPath : null;
  pendingUndo = undefined;
  if (reloadPath) window.location.assign(reloadPath);
}

function showToast(kind, { undoAvailable = kind === "removed" } = {}) {
  const toast = document.querySelector("[data-favorite-toast]");
  if (!toast) return;

  const messages = {
    added: toast.dataset.favoriteAdded,
    removed: toast.dataset.favoriteRemoved,
    restored: toast.dataset.favoriteRestored,
    error: toast.dataset.favoriteError,
  };
  toast.querySelector("[data-favorite-toast-message]").textContent = messages[kind];
  toast.querySelector("[data-favorite-toast-link]").hidden = kind !== "added";
  const undoButton = toast.querySelector("[data-favorite-toast-undo]");
  undoButton.hidden = !undoAvailable;
  undoButton.disabled = false;
  undoButton.setAttribute("aria-busy", "false");
  toast.querySelector("[data-favorite-toast-close]").disabled = false;
  toast.hidden = false;

  clearToastTimer();
  toastPauseReasons.clear();
  if (toast.matches(":hover")) toastPauseReasons.add("pointer");
  if (toast.contains(document.activeElement)) toastPauseReasons.add("focus");

  const durationAttribute = undoAvailable ? "favoriteUndoDuration" : "favoriteDuration";
  const duration = Number.parseInt(toast.dataset[durationAttribute], 10);
  toastRemainingMs = Number.isNaN(duration) ? (undoAvailable ? 8000 : 5000) : duration;
  scheduleToastTimer();
}

function closeFavoriteModal({ restoreFocus = true } = {}) {
  const modal = document.querySelector("[data-favorite-modal]");
  if (!modal?.open) return;

  restoreModalFocus = restoreFocus;
  if (typeof modal.close === "function") {
    modal.close();
  } else {
    modal.removeAttribute("open");
    if (restoreFocus) modalTrigger?.focus();
  }
}

function openFavoriteModal(trigger) {
  const modal = document.querySelector("[data-favorite-modal]");
  if (!modal) return;

  const variant = trigger?.dataset.membershipModalContext === "offer" ? "offer" : "favorite";
  modal.querySelectorAll("[data-favorite-modal-variant]").forEach((element) => {
    element.hidden = element.dataset.favoriteModalVariant !== variant;
  });

  modalTrigger = trigger;
  restoreModalFocus = true;
  if (typeof modal.showModal === "function") {
    modal.showModal();
  } else {
    modal.setAttribute("open", "");
  }
  modal.querySelector("[data-favorite-modal-close]")?.focus();
}

async function toggleFavorite(button) {
  if (pendingUndo?.inFlight) return;

  hideToast({ reloadEmptyPage: false });
  const key = button.dataset.favoriteKey;
  const favorited = button.dataset.favorited === "true";
  const buttons = favoriteButtons(key);
  setButtonsBusy(buttons, true);

  try {
    const updatedState = await requestFavorite(button.dataset.favoriteUrl, favorited ? "DELETE" : "POST");
    const removal = setFavoriteState(buttons, updatedState);

    if (updatedState) {
      showToast("added");
    } else {
      pendingUndo = {
        key,
        url: button.dataset.favoriteUrl,
        buttons,
        trigger: button,
        ...removal,
        inFlight: false,
      };
      showToast("removed");
    }
  } catch (_error) {
    showToast("error");
  } finally {
    setButtonsBusy(buttons, false);
  }
}

async function undoFavorite(button) {
  const undo = pendingUndo;
  if (!undo || undo.inFlight) return;

  undo.inFlight = true;
  const closeButton = document.querySelector("[data-favorite-toast-close]");
  const restoreFocus = document.activeElement === button;
  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  if (closeButton) closeButton.disabled = true;
  setButtonsBusy(undo.buttons, true);
  pauseToast("request");

  try {
    const updatedState = await requestFavorite(undo.url, "POST");
    if (!updatedState) throw new Error("Favorite undo failed");
    if (pendingUndo !== undo) return;

    restoreFavoriteState(undo);
    setButtonsBusy(undo.buttons, false);
    pendingUndo = undefined;
    showToast("restored");

    if (restoreFocus) {
      const focusTarget = undo.trigger?.isConnected ? undo.trigger : favoriteButtons(undo.key)[0];
      focusTarget?.focus();
    }
  } catch (_error) {
    if (pendingUndo !== undo) return;

    undo.inFlight = false;
    setButtonsBusy(undo.buttons, false);
    showToast("error", { undoAvailable: true });
  }
}

document.addEventListener("click", function (event) {
  const favorite = event.target.closest?.("[data-favorite-toggle]");
  if (favorite) {
    event.preventDefault();
    if (favorite.dataset.authenticated === "true") {
      toggleFavorite(favorite);
    } else {
      openFavoriteModal(favorite);
    }
    return;
  }

  const undo = event.target.closest?.("[data-favorite-toast-undo]");
  if (undo) {
    event.preventDefault();
    undoFavorite(undo);
    return;
  }

  const membershipTrigger = event.target.closest?.("[data-membership-modal-trigger]");
  if (membershipTrigger) {
    event.preventDefault();
    openFavoriteModal(membershipTrigger);
    return;
  }

  if (event.target.closest?.("[data-favorite-toast-close]")) {
    hideToast();
    return;
  }

  if (event.target.closest?.("[data-favorite-modal-close]")) {
    closeFavoriteModal();
    return;
  }

  const membershipAction = event.target.closest?.("[data-favorite-membership-screen]");
  if (membershipAction) {
    const screenName = membershipAction.dataset.favoriteMembershipScreen;
    const trigger = modalTrigger;
    closeFavoriteModal({ restoreFocus: false });
    openMembershipDrawer(screenName, trigger);
    return;
  }

  const modal = event.target.closest?.("[data-favorite-modal]");
  if (modal && event.target === modal) closeFavoriteModal();
});

document.addEventListener("pointerover", function (event) {
  const toast = event.target.closest?.("[data-favorite-toast]");
  if (toast && !toast.contains(event.relatedTarget)) pauseToast("pointer");
});

document.addEventListener("pointerout", function (event) {
  const toast = event.target.closest?.("[data-favorite-toast]");
  if (toast && !toast.contains(event.relatedTarget)) resumeToast("pointer");
});

document.addEventListener("focusin", function (event) {
  if (event.target.closest?.("[data-favorite-toast]")) pauseToast("focus");
});

document.addEventListener("focusout", function (event) {
  const toast = event.target.closest?.("[data-favorite-toast]");
  if (toast && !toast.contains(event.relatedTarget)) resumeToast("focus");
});

document.addEventListener("close", function (event) {
  if (!event.target.matches?.("[data-favorite-modal]")) return;

  if (restoreModalFocus) modalTrigger?.focus();
  restoreModalFocus = true;
}, true);

document.addEventListener("turbo:before-render", function () {
  hideToast({ reloadEmptyPage: false, force: true });
  closeFavoriteModal({ restoreFocus: false });
});
